import { SponsorMeButton } from "@dogstrust/dt-web-core-lib-front-end/src/components/SponsorMeButton/SponsorMeButton";
import {
	Dislikes,
	Likes,
	QuoteMarks,
} from "@dogstrust/dt-web-core-lib-front-end/src/components/common/Icons";
import type {
	MediaImageProps,
	MediaVideoProps,
} from "@dogstrust/dt-web-core-lib-front-end/src/components/common/MediaCarousel/MediaCarousel";
import { SocialMediaIcons } from "@dogstrust/dt-web-core-lib-front-end/src/components/common/SocialMediaIcons/SocialMediaIcons";
import SEO from "@dogstrust/dt-web-core-lib-front-end/src/components/seo";
import WrappedSection from "@dogstrust/dt-web-core-lib-front-end/src/containers/WrappedSection/WrappedSection";
import IntroSponsorDogProfile from "@dogstrust/dt-web-core-lib-front-end/src/containers/introBlocks/IntroSponsorDogProfile";
import ParagraphSections from "@dogstrust/dt-web-core-lib-front-end/src/containers/paragraphs/ParagraphSections/ParagraphSections";
import { usePageManager } from "@dogstrust/dt-web-core-lib-front-end/src/context";
import { Layout } from "@dogstrust/uk/components/Layout/Layout";

import { usePersistState } from "@dogstrust/dt-web-core-lib-front-end/src/hooks/usePersistState";
import { Colours } from "@dogstrust/dt-web-core-lib-front-end/src/utils/colours";
import { getImagePath } from "@dogstrust/dt-web-core-lib-front-end/src/utils/images";
import { getSeoProps } from "@dogstrust/dt-web-core-lib-front-end/src/utils/seo";

import { type PageProps, graphql, navigate } from "gatsby";
import React, { useEffect, useState } from "react";
import * as styles from "./SADProfilePage.module.scss";

interface SADProfilePageProps extends PageProps {
	data: Queries.SADProfilePageQuery;
}

import { Head as DefaultHead } from "@dogstrust/dt-web-core-lib-front-end/src/components/Head/Head";
export const Head: React.FC<SADProfilePageProps> = ({
	data: {
		nodeSponsorDog: {
			title,
			field_meta_tags,
			path: { alias: profileUrl },
			relationships: { field_featured_image, field_breed },
		},
	},
}) => {
	return (
		<DefaultHead pageUrl={profileUrl}>
			<SEO
				{...getSeoProps(field_meta_tags, {
					title: `Sponsor a ${field_breed[0].name} Dog | ${title} | Dogs Trust`,
					description: `Sponsor ${title} the ${field_breed[0].name} today, one of our dogs who needs that extra loving and care from the Dogs Trust team.`,
					canonical_url: process.env.GATSBY_DOMAIN_NAME + profileUrl,
					og_type: "article",
					og_url: process.env.GATSBY_DOMAIN_NAME + profileUrl,
					og_title: `Sponsor a ${field_breed[0].name} Dog | ${title} | Dogs Trust`,
					og_description: `Sponsor ${title} the ${field_breed[0].name} today, one of our dogs who needs that extra loving and care from the Dogs Trust team.`,
					og_image_url: `${getImagePath(
						"400x300",
						field_featured_image?.field_media_image?.uri,
					)}`,
					twitter_cards_type: "summary_large_image",
					twitter_cards_description: `Sponsor ${title} the ${field_breed[0].name} today, one of our dogs who needs that extra loving and care from the Dogs Trust team.`,
					twitter_cards_site: "@DogsTrust",
					twitter_cards_title: `Sponsor a ${field_breed[0].name} Dog | ${title} | Dogs Trust`,
					twitter_cards_page_url: process.env.GATSBY_DOMAIN_NAME + profileUrl,
					twitter_cards_image: `${getImagePath(
						"400x300",
						field_featured_image?.field_media_image?.uri,
					)}`,
				})}
			/>
		</DefaultHead>
	);
};

export const SADProfilePage: React.FC<SADProfilePageProps> = ({
	data: {
		nodeSponsorDog: {
			apiKey,
			title,
			breadcrumbs,
			field_about_me,
			field_why_i_need_sponsoring,
			fundCode,
			field_quote,
			field_quotee,
			field_quotee_role,
			field_sex,
			field_tag_line,
			rehomingCentre,
			imagePath,
			path: { alias: profileUrl },
			relationships: {
				field_page_sections,
				field_gallery_of_images_or_video,
				field_featured_image,
				field_breed,
				field_likes,
				field_dislikes,
			},
		},
	},
	location,
}) => {
	const [showBanner, setShowBanner] = useState(false);
	const { usePagination, getDogIndex } = usePageManager();
	const pagination = usePersistState<DogsTrust.PaginationProps>(
		"pagination",
		// biome-ignore lint/suspicious/noExplicitAny: Who cares
		(location?.state || ({} as any)).pagination,
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: We only want this to run once
	useEffect(() => {
		if (pagination) {
			usePagination(true);
			getDogIndex(window.location.pathname);
		}
	}, [pagination]);

	const mapMediaToGalleryItems = (): (MediaImageProps | MediaVideoProps)[] => {
		return [field_featured_image, ...field_gallery_of_images_or_video].map(
			(mediaItem) => {
				switch (mediaItem.__typename) {
					case "media__image":
						return {
							type: "image",
							src: mediaItem.field_media_image.uri,
							alt: mediaItem.field_media_image.alt,
						} as MediaImageProps;
					case "media__remote_video":
						return {
							title: mediaItem.name,
							type: "video",
							youtubeId: mediaItem.field_video_id,
						} as MediaVideoProps;
				}
			},
		);
	};
	const onSponsor = (shouldNavigate: boolean) => {
		if (shouldNavigate) {
			navigate(process.env.GATSBY_SAD_BASKET_URL);
		}
	};
	const onRemove = () => {
		setShowBanner(false);
	};

	const sponsorMeButtonSadDog = {
		apiKey,
		id: fundCode, // Same as ID
		fundCode: fundCode, // Same as ID
		name: title,
		amount: "2.00",
		frequency: "weekly",
		imagePath,
		imageBaseUrl: process.env.GATSBY_IMAGES_BASE_URL,
		galleryItems: [field_featured_image, ...field_gallery_of_images_or_video]
			.filter((media) => media.__typename === "media__image")
			.map((image: Queries.MediaImageOrVideoData_media__image_Fragment) => ({
				type: "image",
				src: image.field_media_image.uri,
				alt: image.field_media_image.alt,
			})),
		isGift: false,
		breed: field_breed[0].name,
		gender: field_sex,
		centreName: rehomingCentre.title,
		profileUrl,
	};
	return (
		<Layout breadcrumbs={breadcrumbs ? [...breadcrumbs] : []}>
			{showBanner && (
				<div className={styles.addedtobasketbanner}>
					<div
						className={styles.addedtobasketbannertitle}
					>{`${title} sponsorship added`}</div>
					<div className={styles.addedtobasketbannerlink}>
						<a href={process.env.GATSBY_SAD_BASKET_URL}>Go to basket</a>
					</div>
				</div>
			)}
			<IntroSponsorDogProfile
				title={`Hi, I'm ${title}`}
				body={field_tag_line}
				breed={field_breed[0].name}
				sex={field_sex}
				centreName={rehomingCentre?.title || ""}
				galleryItems={mapMediaToGalleryItems()}
			/>
			<WrappedSection sectionName="SADDetails">
				<div className={styles.dogdetails}>
					<div className={styles.dogdetailstext}>
						{field_about_me?.value?.length > 0 && (
							<div className={styles.textsection}>
								<h2>About me</h2>
								<div
									// biome-ignore lint/security/noDangerouslySetInnerHtml: Safe as this is a CMS controlled field
									dangerouslySetInnerHTML={{
										__html: field_about_me.value,
									}}
								/>
							</div>
						)}
						{field_why_i_need_sponsoring?.value?.length > 0 && (
							<div className={styles.textsection}>
								<h2>Why I need sponsoring</h2>
								<div
									// biome-ignore lint/security/noDangerouslySetInnerHtml: Safe as this is a CMS controlled field
									dangerouslySetInnerHTML={{
										__html: field_why_i_need_sponsoring.value,
									}}
								/>
							</div>
						)}
					</div>
				</div>
			</WrappedSection>
			<WrappedSection sectionName="SADSponsorMeButton">
				<SponsorMeButton
					title={"Sponsor me from £1 a week"}
					sadDog={sponsorMeButtonSadDog}
					onSponsor={onSponsor}
					onRemove={onRemove}
					isH2={true}
				/>
			</WrappedSection>
			{field_quote && field_quotee && field_quotee_role && (
				<WrappedSection
					sectionName="SADQuote"
					sectionColour={Colours.SANDY_FUR}
				>
					<div className={styles.quotesection}>
						<div className={styles.quotessectioninner}>
							<div className={styles.quote}>
								<div className={styles.quotemarks}>
									<QuoteMarks height={"63px"} width={"48px"} />
								</div>
								<p className={styles.quotetext}>
									{field_quote}
									{/* biome-ignore lint/style/noUnusedTemplateLiteral: Using special char */}
									{`\u00A0`}
									<QuoteMarks height={"16px"} width={"11px"} />
								</p>
							</div>
							<p className={styles.quotee}>{field_quotee}</p>
							<p className={styles.quoteerole}>{field_quotee_role}</p>
						</div>
					</div>
				</WrappedSection>
			)}
			<WrappedSection
				sectionName="SADProfilePageLikesDislikes"
				sectionColour={Colours.SUNSHINE_YELLOW}
			>
				<div className={styles.likessection}>
					<div className={styles.likescontent}>
						<div className={styles.likesicon}>
							<Likes />
						</div>
						<div className={styles.likestext}>
							<h2>I love</h2>
							<p>
								{field_likes.map((like) => like.name).join("\u00A0•\u00A0")}
							</p>
						</div>
					</div>

					<div className={styles.likescontent}>
						<div className={styles.likesicon}>
							<Dislikes />
						</div>
						<div className={styles.likestext}>
							<h2>I really don't like</h2>
							<p>
								{field_dislikes.map((like) => like.name).join("\u00A0•\u00A0")}
							</p>
						</div>
					</div>
				</div>
			</WrappedSection>
			{field_page_sections?.map((section, i) => (
				<ParagraphSections
					key={"id" in section ? section.id : i}
					data={section}
					thisSectionKey={i}
				/> // TODO: Figure way of adding key
			))}
			<WrappedSection sectionName="SADSponsorMeButton">
				<SponsorMeButton
					title={"Sponsor me"}
					sadDog={sponsorMeButtonSadDog}
					onSponsor={onSponsor}
					onRemove={onRemove}
					isH2={true}
				/>
			</WrappedSection>
			<WrappedSection sectionName="SADProfilePageSocialMedia">
				<div className={styles.socialmediawrapper}>
					<div className={styles.sharetolabel}>
						Do you know someone else that can help?
					</div>
					<SocialMediaIcons
						className={styles.sharetoicons}
						invert={true}
						xUrl={`https://twitter.com/intent/tweet?url=${
							process.env.GATSBY_DOMAIN_NAME + profileUrl
						}&via=DogsTrust`}
						facebookUrl={`https://www.facebook.com/sharer/sharer.php?u=${
							process.env.GATSBY_DOMAIN_NAME + profileUrl
						}`}
						mailToUrl={`mailto:?subject=I saw this on the Dogs Trust’s website...&body=I saw this on the Dogs Trust’s website and thought of you ${
							process.env.GATSBY_DOMAIN_NAME + profileUrl
						}`}
						whatsAppUrl={`https://wa.me/?text=I saw this on the Dogs Trust’s website and thought of you: ${
							process.env.GATSBY_DOMAIN_NAME + profileUrl
						}`}
					/>
				</div>
			</WrappedSection>
		</Layout>
	);
};
export default SADProfilePage;

export const query = graphql`
	
    query SADProfilePage($path: String) {
        nodeSponsorDog(path: { alias: { eq: $path } }) {
            ...SponsorDogData
        }
    }
`;

export const fragment = graphql`
    fragment SponsorDogData on node__sponsor_dog {
		apiKey
		profileUrl
        title
        path {
            alias
        }
        breadcrumbs {
            ...BreadCrumb
        }
        field_about_me {
            value
        }
        field_can_be_visited
        field_date
        fundCode
        field_quote
        field_quotee
        field_quotee_role
        field_sex
        field_tag_line
        field_promo_short_text
		imagePath
        field_meta_tags {
            ...MetaTags
        }
        rehomingCentre {
            title
            field_latitude
            field_longitude
        }
        field_why_i_need_sponsoring {
            value
        }
        relationships {
            field_breed {
                name
            }
            field_centre_code {
                name
            }
            field_likes {
                name
            }
            field_dislikes {
                name
            }
            field_featured_image {
                ...MediaImageData
            }
            field_gallery_of_images_or_video {
                ...MediaImageOrVideoData
            }
            field_page_sections {
                ...ParagraphSectionsData
            }
        }
    }
`;
